import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { VARIABLE_TEXT_FEATURE_FLAG_ALL, VariableTextFeatureFlag } from '@makyoapp/makyo-common'

import { TEXT_ABLE_COLUMN_TYPES_TEXT_ONLY } from '@/shared/builder-constants'

import { BuilderComponentGroup, BuilderComponentSettingType, ComponentSettingKey, SpacingInputUnit } from '../types'
import { LABELS } from '../types/labels'
import {
  makeClampWidthItems,
  makeDataSource,
  makeFilter,
  makeTypographyGroup,
  makeWidthItems,
} from '../utils/components'

const textOnlyColumnTypes = TEXT_ABLE_COLUMN_TYPES_TEXT_ONLY.filter((type) => type !== ColumnTypes.Slug)

export const ChartComponents: BuilderComponentGroup = {
  id: 'chart',
  title: LABELS.CHARTS_KEY,
  components: [
    {
      compId: 'pie',
      title: LABELS.PIE_KEY,
      searchKeywords: ['pie chart', 'donut chart', 'circle chart', 'graph', 'statistics', 'analytics'],
      icon: 'ChartPie24',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.PieChartDataSource),
              {
                id: ComponentSettingKey.PieChartDataLabelColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.LABEL_KEY,
                help: 'All column types are supported',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PieChartDataSource,
                  columnTypes: textOnlyColumnTypes,
                },
              },
              {
                id: ComponentSettingKey.PieChartDataValueColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.VALUES_KEY,
                help: 'Only numeric columns are supported',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.PieChartDataSource,
                  columnTypes: [ColumnTypes.Number, ColumnTypes.Function],
                },
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PieChartDataHeading,
                type: BuilderComponentSettingType.VariableText,
                label: '',
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                variableTextSettings: {
                  flags: VARIABLE_TEXT_FEATURE_FLAG_ALL & ~VariableTextFeatureFlag.ColumnData,
                },
              },
            ],
          },
          [LABELS.SORT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PieChartDataSortBy,
                label: LABELS.SORT_BY_KEY,
                type: BuilderComponentSettingType.SortBySelect,
                sortBySelectSettings: {
                  tableSourceId: ComponentSettingKey.PieChartDataSource,
                },
              },
              {
                id: ComponentSettingKey.PieChartDataSortType,
                label: LABELS.SORT_ORDERING_KEY,
                defaultValue: 'asc',
                type: BuilderComponentSettingType.IconSelect,
                iconSelectSettings: {
                  options: [
                    {
                      id: 'asc',
                      title: LABELS.ASCENDING_KEY,
                      icon: 'Asc16',
                    },
                    {
                      id: 'desc',
                      title: LABELS.DESCENDING_KEY,
                      icon: 'Desc16',
                    },
                  ],
                },
                dependOn: {
                  id: ComponentSettingKey.PieChartDataSortBy,
                  showWhen: (v) => v !== '#random#',
                },
              },
            ],
          },
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.PieChartDataFilter,
            ComponentSettingKey.PieChartDataSource
          ),
        },
        designGroup: {
          [LABELS.ITEMS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PieChartDataItemsPerPage,
                label: LABELS.MAX_ITEMS_KEY,
                type: BuilderComponentSettingType.Slider,
                defaultValue: [10],
                sliderSettings: {
                  max: 50,
                  min: 1,
                  step: 1,
                },
              },
            ],
          },
          [LABELS.COLOR_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PieChartDesignColorSequence,
                type: BuilderComponentSettingType.Select,
                label: LABELS.COLOR_SEQUENCE_KEY,
                defaultValue: 'random',
                selectSettings: {
                  options: [
                    {
                      value: 'sequential',
                      label: 'Sequential',
                    },
                    {
                      value: 'random',
                      label: 'Random',
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.FEATURES_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PieChartDesignLegend,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.LEGEND_KEY,
                defaultValue: true,
              },
              {
                id: ComponentSettingKey.PieChartDesignLabel,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.LABELS_KEY,
                defaultValue: false,
              },
            ],
          },
          [LABELS.STYLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PieChartDesignDoughnut,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.DOUGHNUT_KEY,
                defaultValue: false,
              },
              // {
              //   id: ComponentSettingKey.PieChartDesignAbbrNumber,
              //   type: BuilderComponentSettingType.Switch,
              //   label: LABELS.ABBREVIATE_NUMBER_KEY,
              //   defaultValue: true,
              // },
            ],
          },
          [LABELS.DIMENSION_KEY]: makeClampWidthItems(ComponentSettingKey.PieChartDesignWidth),
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PieChartDesignAlignment,
                type: BuilderComponentSettingType.ComponentAlignment,
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.PieChartDesignHeadingAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: LABELS.COMPONENT_TITLE_KEY,
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.PieChartDesignComponentMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_KEY,
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 4,
                //     [SpacingInputUnit.Rem]: 4,
                //     [SpacingInputUnit.Pixel]: Infinity,
                //     [SpacingInputUnit.Percent]: 10,
                //   },
                // },
              },
              {
                id: ComponentSettingKey.PieChartDesignHeadingMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_TITLE_KEY,
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 4,
                //     [SpacingInputUnit.Rem]: 4,
                //     [SpacingInputUnit.Pixel]: Infinity,
                //     [SpacingInputUnit.Percent]: 10,
                //   },
                // },
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.PieChartDesignTypography, [
            {
              id: ComponentSettingKey.PieChartDesignHeadingTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'headline-small',
            },
          ]),
        },
        behavior: {
          visibility: true,
        },
      },
    },
    {
      compId: 'bar',
      title: LABELS.BAR_KEY,
      searchKeywords: ['bar chart', 'histogram', 'graph', 'statistics', 'analytics', 'report chart'],
      icon: 'ChartBar24',
      nested: false,
      settings: {
        dataGroup: {
          [LABELS.DATA_SOURCE_KEY]: {
            items: [
              makeDataSource(ComponentSettingKey.BarChartDataSource),
              {
                id: ComponentSettingKey.BarChartDataXAxisColumn,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.LABELS_KEY,
                help: 'Select a text column to display labels for the values.',
                columnSelectSettings: {
                  columnTypes: textOnlyColumnTypes,
                  tableSourceId: ComponentSettingKey.BarChartDataSource,
                },
              },
              {
                id: ComponentSettingKey.BarChartDataValueColumns,
                type: BuilderComponentSettingType.ColumnSelect,
                label: LABELS.VALUES_KEY,
                help: 'Select one or more numeric columns.',
                columnSelectSettings: {
                  tableSourceId: ComponentSettingKey.BarChartDataSource,
                  columnTypes: [ColumnTypes.Number, ColumnTypes.Function],
                  multiple: true,
                },
              },
            ],
          },
          [LABELS.COMPONENT_TITLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.BarChartDataHeading,
                type: BuilderComponentSettingType.VariableText,
                label: '',
                defaultValue: [
                  {
                    type: 'paragraph',
                    children: [{ text: '' }],
                  },
                ],
                variableTextSettings: {
                  flags: VARIABLE_TEXT_FEATURE_FLAG_ALL & ~VariableTextFeatureFlag.ColumnData,
                },
              },
            ],
          },
          [LABELS.SORT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.BarChartDataSortBy,
                label: LABELS.SORT_BY_KEY,
                type: BuilderComponentSettingType.SortBySelect,
                sortBySelectSettings: {
                  tableSourceId: ComponentSettingKey.BarChartDataSource,
                },
              },
              {
                id: ComponentSettingKey.BarChartDataSortType,
                label: LABELS.SORT_ORDERING_KEY,
                defaultValue: 'asc',
                type: BuilderComponentSettingType.IconSelect,
                iconSelectSettings: {
                  options: [
                    {
                      id: 'asc',
                      title: LABELS.ASCENDING_KEY,
                      icon: 'Asc16',
                    },
                    {
                      id: 'desc',
                      title: LABELS.DESCENDING_KEY,
                      icon: 'Desc16',
                    },
                  ],
                },
                dependOn: {
                  id: ComponentSettingKey.BarChartDataSortBy,
                  showWhen: (v) => v !== '#random#',
                },
              },
            ],
          },
          [LABELS.DATA_FILTER_KEY]: makeFilter(
            ComponentSettingKey.BarChartDataFilter,
            ComponentSettingKey.BarChartDataSource
          ),
        },
        designGroup: {
          [LABELS.ALIGNMENT_KEY]: {
            items: [
              {
                id: ComponentSettingKey.BarChartDesignAlignment,
                type: BuilderComponentSettingType.ComponentAlignment,
                label: LABELS.COMPONENT_ALIGNMENT_KEY,
              },
              {
                id: ComponentSettingKey.BarChartDesignHeadingAlignment,
                type: BuilderComponentSettingType.HorizontalAlignment,
                label: LABELS.COMPONENT_TITLE_KEY,
              },
            ],
          },
          [LABELS.COLOR_KEY]: {
            items: [
              {
                id: ComponentSettingKey.BarChartDesignColorSequence,
                type: BuilderComponentSettingType.Select,
                label: LABELS.COLOR_SEQUENCE_KEY,
                defaultValue: 'random',
                selectSettings: {
                  options: [
                    {
                      value: 'sequential',
                      label: 'Sequential',
                    },
                    {
                      value: 'random',
                      label: 'Random',
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.DIMENSION_KEY]: makeClampWidthItems(ComponentSettingKey.BarChartDesignWidth, {
            help: 'It may take a few moments for the component to adjust its width',
          }),
          [LABELS.ITEMS_KEY]: {
            items: [
              {
                id: ComponentSettingKey.BarChartDataItemsPerPage,
                label: LABELS.MAX_ITEMS_KEY,
                type: BuilderComponentSettingType.Slider,
                defaultValue: [10],
                sliderSettings: {
                  max: 50,
                  min: 1,
                  step: 1,
                },
              },
            ],
          },
          [LABELS.TYPOGRAPHY_KEY]: makeTypographyGroup(ComponentSettingKey.BarChartDesignTypography, [
            {
              id: ComponentSettingKey.BarChartDesignHeadingTypography,
              label: LABELS.COMPONENT_TITLE_KEY,
              defaultValue: 'headline-small',
            },
            // {
            //   id: ComponentSettingKey.BarChartDesignXAxisTypography,
            //   label: LABELS.LABELS_KEY,
            //   defaultValue: 'Body Small',
            // },
            // {
            //   id: ComponentSettingKey.BarChartDesignValuesTypography,
            //   label: 'Values',
            //   defaultValue: 'Body Small',
            // },
          ]),
          [LABELS.FEATURES_KEY]: {
            items: [
              {
                id: ComponentSettingKey.BarChartDesignLegend,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.LEGEND_KEY,
                defaultValue: true,
              },
              {
                id: ComponentSettingKey.BarChartDesignShowGrid,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.SHOW_GRID_KEY,
                defaultValue: true,
              },
              {
                id: ComponentSettingKey.BarChartDesignShowAxis,
                type: BuilderComponentSettingType.Switch,
                label: LABELS.SHOW_AXIS_KEY,
                defaultValue: false,
              },
            ],
          },
          [LABELS.ORIENTATION_KEY]: {
            items: [
              {
                id: ComponentSettingKey.BarChartDesignHorizontal,
                label: LABELS.ORIENTATION_KEY,
                type: BuilderComponentSettingType.IconSelect,
                defaultValue: 'vertical',
                iconSelectSettings: {
                  options: [
                    {
                      id: 'horizontal',
                      icon: 'Horizontal24',
                      title: LABELS.HORIZONTAL_KEY,
                    },
                    {
                      id: 'vertical',
                      icon: 'Vertical24',
                      title: LABELS.VERTICAL_KEY,
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.BAR_STYLE_KEY]: {
            items: [
              {
                id: ComponentSettingKey.BarChartDesignStyle,
                type: BuilderComponentSettingType.Select,
                label: '',
                defaultValue: 'stacked',
                selectSettings: {
                  options: [
                    {
                      value: 'grouped',
                      label: LABELS.GROUPED_KEY,
                    },
                    {
                      value: 'stacked',
                      label: LABELS.STACKED_KEY,
                    },
                  ],
                },
              },
            ],
          },
          [LABELS.SPACING_KEY]: {
            items: [
              {
                id: ComponentSettingKey.BarChartDesignHeadingMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_TITLE_KEY,
                defaultValue: {
                  top: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 0,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 4,
                //     [SpacingInputUnit.Rem]: 4,
                //     [SpacingInputUnit.Pixel]: Infinity,
                //     [SpacingInputUnit.Percent]: 10,
                //   },
                // },
              },
              {
                id: ComponentSettingKey.BarChartDesignCompMargin,
                type: BuilderComponentSettingType.Spacing,
                label: LABELS.COMPONENT_KEY,
                defaultValue: {
                  top: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                  bottom: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                  left: {
                    value: 20,
                    unit: SpacingInputUnit.Pixel,
                  },
                  right: {
                    value: 10,
                    unit: SpacingInputUnit.Pixel,
                  },
                },
                // spacingSettings: {
                //   max: {
                //     [SpacingInputUnit.Em]: 4,
                //     [SpacingInputUnit.Rem]: 4,
                //     [SpacingInputUnit.Pixel]: Infinity,
                //     [SpacingInputUnit.Percent]: 10,
                //   },
                // },
              },
              // {
              //   id: ComponentSettingKey.BarChartDesignLegendGap,
              //   label: LABELS.LEGEND_GAP_KEY,
              //   type: BuilderComponentSettingType.Slider,
              //   defaultValue: [16],
              //   sliderSettings: {
              //     max: 100,
              //     min: 0,
              //     step: 1,
              //   },
              // },
            ],
          },
        },
        behavior: {
          visibility: true,
        },
      },
    },
  ],
}
